import { Button } from '@daily/shared/components/Button';
import { LandscapeIcon } from '@daily/shared/components/Icons/LandscapeIcon';
import { UserIcon } from '@daily/shared/components/Icons/UserIcon';
import { Stack } from '@daily/shared/components/Stack';
import { Text } from '@daily/shared/components/Text';
import { Tooltip } from '@daily/shared/components/Tooltip';
import { VisuallyHidden } from '@daily/shared/components/VisuallyHidden';
import { useTheme } from '@daily/shared/contexts/Theme';
import { useLocalSessionId, useVideoTrack } from '@daily-co/daily-react';
import classNames from 'classnames';
import getConfig from 'next/config';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useBackgroundEffect } from '../../hooks/useBackgroundEffect';

export const BackgroundEffects: React.FC<React.PropsWithChildren<unknown>> =
  () => {
    const { t } = useTranslation();
    const { colors } = useTheme();
    const { assetPrefix } = getConfig().publicRuntimeConfig;

    const localSessionId = useLocalSessionId();
    const videoTrack = useVideoTrack(localSessionId);

    const isUserFacingVideo = useMemo(() => {
      if (!videoTrack?.track) return true;
      const videoTrackSettings = videoTrack.track.getSettings();
      return 'facingMode' in videoTrackSettings
        ? videoTrackSettings.facingMode === 'user'
        : true;
    }, [videoTrack?.track]);

    const { currentEffect, effects, selectEffect } = useBackgroundEffect();

    return (
      <div
        className={classNames('effects', {
          isUserFacingVideo,
        })}
      >
        <Stack align="center" justify="space-between" horizontal wrap>
          <Text variant="strong">{t('settings.modal.selectEffect')}</Text>
          {currentEffect && (
            <Text>
              {t('settings.modal.appliedEffect', {
                effect: currentEffect,
              })}
            </Text>
          )}
        </Stack>
        <div className="selection">
          {effects.map((effect) => (
            <Tooltip
              key={effect}
              id={`tt-eff-${effect}`}
              placement="top"
              // i18next-extract-mark-context-next-line ["none", "strong-blur", "soft-blur", "vb-coffeeshop", "vb-forest", "vb-hills", "vb-library", "vb-lounge", "vb-ocean", "vb-office", "vb-palms", "vb-rollercoaster"]
              title={t(`settings.modal.effects`, { context: effect })}
              tabIndex={-1}
            >
              <Button
                id={`background-effect-${effect}`}
                className={classNames('btn-effect', effect, {
                  vb: effect.startsWith('vb-'),
                  selected: currentEffect === effect,
                })}
                data-testid={`btn-${effect}`}
                onClick={() => selectEffect(effect)}
                variant="secondary"
              >
                {effect === 'none' ? (
                  <>
                    <UserIcon
                      className={`icon-${effect}`}
                      color={
                        currentEffect === effect ? colors.accent : undefined
                      }
                      size={24}
                    />
                    <VisuallyHidden>
                      {t(`settings.modal.effects_none`)}
                    </VisuallyHidden>
                  </>
                ) : effect.endsWith('-blur') ? (
                  <>
                    <LandscapeIcon
                      className={`icon-${effect}`}
                      color={
                        currentEffect === effect ? colors.accent : undefined
                      }
                      size={24}
                    />
                    <VisuallyHidden>
                      {
                        // i18next-extract-mark-context-next-line ["strong-blur", "soft-blur"]
                        t(`settings.modal.effects`, { context: effect })
                      }
                    </VisuallyHidden>
                  </>
                ) : effect.startsWith('vb-') ? (
                  <img
                    // i18next-extract-mark-context-next-line ["vb-coffeeshop", "vb-forest", "vb-hills", "vb-library", "vb-lounge", "vb-ocean", "vb-office", "vb-palms", "vb-rollercoaster"]
                    alt={t('settings.modal.effects', {
                      context: effect,
                    })}
                    src={`${assetPrefix}/backgrounds/${effect}-thumb.jpg`}
                    height={56}
                    width={56}
                  />
                ) : null}
              </Button>
            </Tooltip>
          ))}
        </div>
        <style jsx>{`
          .effects {
            display: flex;
            flex-direction: column;
            gap: 16px;
          }
          .selection {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            gap: 8px;
          }
          .selection :global(.btn-effect) {
            height: 56px;
            padding: 15px;
            width: 56px;
          }
          .selection :global(.btn-effect.vb) {
            padding: 0;
            overflow: hidden;
          }
          .isUserFacingVideo .selection :global(.btn-effect.vb) {
            transform: scaleX(-1);
          }
          .selection :global(.btn-effect.selected) {
            border-color: ${colors.accent};
          }
          .selection :global(.icon-soft-blur) {
            filter: blur(1px);
          }
          .selection :global(.icon-strong-blur) {
            filter: blur(3px);
          }
        `}</style>
      </div>
    );
  };
